import React, { useEffect, useRef, useState } from "react";
import { Loader2, LogOut, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { certificationService, formatApiError, resolveImageUrl } from "../../services/catalogService";

const inputCls = "h-11 bg-white border border-black/15 px-3 text-sm focus:outline-none focus:border-[#D92A47]";

// Task 14 — certifications administration: names, subtitles and official logos.
export default function CertificationsPanel({ admin, onLogout }) {
  const [certs, setCerts] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | cert
  const [form, setForm] = useState({ name: "", subtitle: "" });
  const [busy, setBusy] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const reload = () => {
    certificationService.list().then(setCerts).catch((e) => toast.error("Could not load", { description: formatApiError(e) }));
  };
  useEffect(() => { reload(); }, []);

  const startEdit = (c) => { setEditing(c); setForm({ name: c.name, subtitle: c.subtitle || "" }); };
  const startNew = () => { setEditing("new"); setForm({ name: "", subtitle: "" }); };
  const cancel = () => setEditing(null);

  const save = async () => {
    setBusy(true);
    try {
      if (editing === "new") {
        await certificationService.create(form);
        toast.success("Certification added", { description: form.name });
      } else {
        await certificationService.update(editing.id, form);
        toast.success("Certification saved", { description: form.name });
      }
      cancel();
      reload();
    } catch (err) {
      toast.error("Could not save", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await certificationService.remove(id);
      setConfirmId(null);
      reload();
      toast.success("Certification deleted");
    } catch (err) {
      toast.error("Could not delete", { description: formatApiError(err) });
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || editing === "new") return;
    setUploading(true);
    try {
      const updated = await certificationService.uploadLogo(editing.id, file);
      setEditing(updated);
      reload();
      toast.success("Logo updated");
    } catch (err) {
      toast.error("Upload failed", { description: formatApiError(err) });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="admin-certifications-panel">
      <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
        <div>
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-[#D92A47]">Admin</span>
          <h1 className="font-serif text-4xl md:text-5xl text-[#252525] mt-2">Certifications</h1>
          <p className="text-[#817B74] text-sm mt-1">
            Upload the official logos — they replace the text placeholders on the home. Signed in as {admin.email}
          </p>
        </div>
        <button onClick={onLogout} data-testid="admin-logout"
          className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.14em] hover:border-[#D92A47] hover:text-[#D92A47]">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      {certs === null ? (
        <div className="text-[#817B74] py-20 text-center"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>
      ) : editing === null ? (
        <>
          <button onClick={startNew} data-testid="certification-new"
            className="mb-6 inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a]">
            <Plus size={14} /> New certification
          </button>
          <div className="space-y-4">
            {certs.map((c) => (
              <div key={c.id} className="bg-white border border-black/10 p-4 flex items-center gap-4" data-testid={`certification-row-${c.id}`}>
                <div className="h-14 w-20 bg-white border border-black/10 grid place-items-center shrink-0 overflow-hidden">
                  {c.logo ? <img src={resolveImageUrl(c.logo)} alt="" className="max-h-10 max-w-full object-contain" />
                    : <span className="font-serif text-sm">{c.name.slice(0, 2)}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-lg text-[#252525] truncate">{c.name}</p>
                  <p className="text-[11px] uppercase tracking-[0.12em] text-[#817B74]">{c.subtitle || "—"}</p>
                </div>
                <button onClick={() => startEdit(c)} data-testid={`certification-edit-${c.id}`} aria-label={`Edit ${c.name}`}
                  className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                  <Pencil size={15} />
                </button>
                {confirmId === c.id ? (
                  <button onClick={() => remove(c.id)} data-testid={`certification-delete-confirm-${c.id}`}
                    className="h-10 px-4 bg-[#D92A47] text-white text-[11px] font-semibold uppercase tracking-[0.12em]">
                    Confirm delete
                  </button>
                ) : (
                  <button onClick={() => setConfirmId(c.id)} data-testid={`certification-delete-${c.id}`} aria-label={`Delete ${c.name}`}
                    className="grid h-10 w-10 place-items-center border border-black/15 text-[#817B74] hover:border-[#D92A47] hover:text-[#D92A47]">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-white border border-black/10 p-6 md:p-8" data-testid="certification-form">
          <h2 className="font-serif text-2xl mb-6">{editing === "new" ? "New certification" : `Edit: ${editing.name}`}</h2>

          {editing !== "new" && (
            <div className="mb-6 flex items-center gap-4">
              <div className="h-20 w-32 bg-white border border-black/10 grid place-items-center overflow-hidden">
                {editing.logo && <img src={resolveImageUrl(editing.logo)} alt="" className="max-h-16 max-w-full object-contain" data-testid="certification-logo-preview" />}
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="certification-logo-upload"
                className="inline-flex items-center gap-2 h-10 px-4 border border-black/15 text-[11px] uppercase tracking-[0.12em] hover:border-[#D92A47] hover:text-[#D92A47] disabled:opacity-60">
                {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {uploading ? "Uploading…" : "Upload official logo"}
              </button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                onChange={uploadLogo} data-testid="certification-logo-input" />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Name — required</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="certification-field-name" className={`w-full ${inputCls}`} />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.14em] text-[#817B74] mb-1">Subtitle</span>
              <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} placeholder="e.g. CERTIFIED" data-testid="certification-field-subtitle" className={`w-full ${inputCls}`} />
            </label>
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={save} disabled={busy || !form.name.trim()} data-testid="certification-save"
              className="inline-flex items-center gap-2 h-11 px-6 bg-[#D92A47] text-[#F6F2EC] text-[12px] font-semibold uppercase tracking-[0.14em] hover:bg-[#b71e3a] disabled:opacity-50">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : editing === "new" ? "Add certification" : "Save certification"}
            </button>
            <button onClick={cancel} data-testid="certification-cancel"
              className="h-11 px-5 border border-black/15 text-[12px] uppercase tracking-[0.14em] hover:border-[#D92A47]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
